import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Pagination,
  CircularProgress,
  Container
} from "@mui/material";
import {
  ArrowBack,
  Save,
  Search,
  Add,
  Remove,
  CheckCircle,
} from "@mui/icons-material";
import { returnAPI, salesAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const NewReturn = () => {
  const [searchParams] = useSearchParams();
  const saleInvoiceId = searchParams.get("saleInvoice");

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [searchInvoice, setSearchInvoice] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(!saleInvoiceId);
  const [returnItems, setReturnItems] = useState([]);
  const [applyOriginalDiscount, setApplyOriginalDiscount] = useState(true);
  const [refundMethod, setRefundMethod] = useState("cash");
  const [customRefundAmount, setCustomRefundAmount] = useState("");
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  // Pagination state for invoice search
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const steps = [
    "Select Invoice",
    "Select Items",
    "Configure Refund",
    "Review & Submit",
  ];

  useEffect(() => {
    if (saleInvoiceId) {
      checkEligibility(saleInvoiceId);
    } else {
      setLoading(false);
    }
  }, [saleInvoiceId]);

  // Debounced invoice search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInvoice.trim() !== "") {
        searchInvoices();
      } else {
        setInvoices([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
          pages: 0,
        }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInvoice, pagination.page]);

  const checkEligibility = async (invoiceId) => {
    try {
      setLoading(true);
      const response = await returnAPI.checkEligibility(invoiceId);
      setEligibilityData(response.data.data);

      const initialItems = response.data.data.eligibleItems.map((item) => ({
        ...item,
        returnQuantity: 0,
        returnReason: "",
        isDamaged: false,
      }));

      setReturnItems(initialItems);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error checking return eligibility",
        { variant: "error" }
      );
      navigate("/hospital/pharmacy/returns");
    } finally {
      setLoading(false);
    }
  };

  const searchInvoices = async () => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchInvoice,
        status: "confirmed",
      };

      const response = await salesAPI.getAll(params);
      const { salesInvoices: invoicesData, pagination: paginationData } =
        response.data.data;

      setInvoices(invoicesData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error searching invoices", { variant: "error" });
    }
  };

  const handleSearchInvoice = (value) => {
    setSearchInvoice(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleInvoiceSelect = (invoice) => {
    checkEligibility(invoice._id);
    setShowInvoiceSearch(false);
    setActiveStep(1);
  };

  const handleReturnQuantityChange = (index, quantity) => {
    const updatedItems = [...returnItems];
    const maxQuantity = updatedItems[index].remainingQuantity;

    if (quantity <= maxQuantity && quantity >= 0) {
      updatedItems[index].returnQuantity = quantity;
      setReturnItems(updatedItems);
    }
  };

  const handleReasonChange = (index, reason) => {
    const updatedItems = [...returnItems];
    updatedItems[index].returnReason = reason;
    setReturnItems(updatedItems);
  };

  const handleGeneralReasonChange = (reason) => {
    setReturnReason(reason);
    // Also set the general reason for all items that have return quantity
    const updatedItems = returnItems.map((item) => ({
      ...item,
      returnReason: item.returnQuantity > 0 ? reason : item.returnReason,
    }));
    setReturnItems(updatedItems);
  };

  const handleDamageChange = (index, isDamaged) => {
    const updatedItems = [...returnItems];
    updatedItems[index].isDamaged = isDamaged;
    setReturnItems(updatedItems);
  };

  const calculateReturnTotals = () => {
    const itemsToReturn = returnItems.filter((item) => item.returnQuantity > 0);

    let totalReturnValue = 0;
    const itemsWithRefund = itemsToReturn.map((item) => {
      // Calculate return item value with tax
      const itemSubtotal = item.returnQuantity * item.sellingPrice;
      const itemTax = itemSubtotal * (item.taxRate / 100);
      const itemTotalValue = itemSubtotal + itemTax;

      totalReturnValue += itemTotalValue;

      return {
        ...item,
        itemSubtotal,
        itemTax,
        itemTotalValue,
      };
    });

    // Calculate actual refund based on payment scenario
    const currentPaid = eligibilityData?.saleInvoice?.paidAmount || 0;
    const currentDue = eligibilityData?.saleInvoice?.dueAmount || 0;
    const paymentStatus = eligibilityData?.saleInvoice?.paymentStatus;

    let actualRefundAmount = 0;
    let newPaidAmount = currentPaid;
    let newDueAmount = currentDue;

    if (paymentStatus === "paid") {
      // Case 3: Fully paid - refund full return value
      actualRefundAmount = totalReturnValue;
      newPaidAmount = currentPaid - actualRefundAmount;
    } else if (paymentStatus === "partially_paid") {
      // Case 1 & 2: Partially paid
      if (totalReturnValue <= currentDue) {
        // Case 1: Return value <= Due amount - adjust due only
        newDueAmount = currentDue - totalReturnValue;
        actualRefundAmount = 0;
      } else {
        // Case 2: Return value > Due amount - settle due first, then refund remaining
        const excessAmount = totalReturnValue - currentDue;
        newDueAmount = 0;
        actualRefundAmount = excessAmount;
        newPaidAmount = currentPaid; //currentPaid - actualRefundAmount;
      }
    } else if (paymentStatus === "pending") {
      // Case 4: Zero payment - adjust due only
      newDueAmount = currentDue - totalReturnValue;
      actualRefundAmount = 0;
    }

    const finalRefundAmount = customRefundAmount
      ? parseFloat(customRefundAmount)
      : actualRefundAmount;

    const totalCost = itemsToReturn.reduce(
      (sum, item) => sum + item.returnQuantity * item.costPrice,
      0
    );

    const netGainLoss = finalRefundAmount - totalCost;

    return {
      itemsToReturn: itemsWithRefund,
      totalReturnValue,
      actualRefundAmount,
      finalRefundAmount,
      totalCost,
      netGainLoss,
      itemCount: itemsToReturn.length,
      paymentUpdate: {
        currentPaid,
        currentDue,
        newPaid: newPaidAmount,
        newDue: newDueAmount,
        paymentStatus,
      },
    };
  };

  const validateQuantities = () => {
    for (const item of returnItems) {
      if (item.returnQuantity > item.remainingQuantity) {
        enqueueSnackbar(
          `Return quantity for ${item.medicine.name} exceeds available quantity`,
          { variant: "error" }
        );
        return false;
      }
      if (item.returnQuantity < 0) {
        enqueueSnackbar(
          `Return quantity for ${item.medicine.name} cannot be negative`,
          { variant: "error" }
        );
        return false;
      }
    }
    return true;
  };

  const validateReturn = () => {
    const { itemsToReturn, finalRefundAmount, paymentUpdate } =
      calculateReturnTotals();

    if (itemsToReturn.length === 0) {
      enqueueSnackbar("Please select at least one item to return", {
        variant: "warning",
      });
      return false;
    }

    if (!returnReason.trim()) {
      enqueueSnackbar("Please provide a general return reason", {
        variant: "warning",
      });
      return false;
    }

    for (const item of itemsToReturn) {
      if (!item.returnReason.trim()) {
        enqueueSnackbar(
          `Please provide a return reason for ${item.medicine.name}`,
          { variant: "warning" }
        );
        return false;
      }
    }

    // Validate custom refund amount doesn't exceed actual refund amount
    if (
      customRefundAmount &&
      parseFloat(customRefundAmount) > paymentUpdate.currentPaid
    ) {
      enqueueSnackbar(
        `Custom refund amount (₹${parseFloat(customRefundAmount).toFixed(
          2
        )}) exceeds paid amount (₹${paymentUpdate.currentPaid.toFixed(
          2
        )})`,
        { variant: "error" }
      );
      return false;
    }

    if (
      customRefundAmount &&
      parseFloat(customRefundAmount) > finalRefundAmount
    ) {
      enqueueSnackbar(
        `Custom refund amount (₹${parseFloat(customRefundAmount).toFixed(
          2
        )}) exceeds maximum allowed refund (₹${finalRefundAmount.toFixed(2)})`,
        { variant: "error" }
      );
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (activeStep === 1 && (!validateReturn() || !validateQuantities())) {
      return;
    }

    if (activeStep === 2) {
      // When moving from step 3 to 4, open confirmation instead of showing step 4 content
      setConfirmationOpen(true);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0 && saleInvoiceId) {
      navigate("/hospital/pharmacy/returns");
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmitReturn = async () => {
    setSaving(true);
    try {
      const { itemsToReturn, finalRefundAmount } = calculateReturnTotals();

      // Prepare items according to backend model requirements
      const processedItems = itemsToReturn.map((item) => {
        return {
          medicine: item.medicine._id,
          batchStock: item.batchStock?._id,
          quantity: item.returnQuantity,
          returnReason: item.returnReason,
          isDamaged: item.isDamaged,
        };
      });

      const returnData = {
        saleInvoice: eligibilityData.saleInvoice._id,
        items: processedItems,
        refundAmount: finalRefundAmount,
        refundMethod,
        reason: returnReason,
        notes: `Return processed by ${user.name}. Payment impact: Paid ₹${eligibilityData.saleInvoice.paidAmount
          } → ₹${calculateReturnTotals().paymentUpdate.newPaid.toFixed(
            2
          )}, Due ₹${eligibilityData.saleInvoice.dueAmount
          } → ₹${calculateReturnTotals().paymentUpdate.newDue.toFixed(2)}`,
      };

      await returnAPI.create(returnData);

      enqueueSnackbar("Return request created successfully!", {
        variant: "success",
      });

      setConfirmationOpen(false);

      // Navigate to returns list after successful creation
      setTimeout(() => {
        navigate("/hospital/pharmacy/returns");
      }, 1000);
    } catch (error) {
      console.error("Return creation error:", error);
      enqueueSnackbar(
        error.response?.data?.message || "Error creating return request",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const {
    itemsToReturn,
    totalReturnValue,
    actualRefundAmount,
    finalRefundAmount,
    totalCost,
    netGainLoss,
    itemCount,
    paymentUpdate,
  } = calculateReturnTotals();

  const maxRefundable = eligibilityData?.saleInvoice?.maxRefundable || 0;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box>
          {/* Header and Stepper */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                startIcon={<ArrowBack sx={{ fontSize: '1.3rem' }} />}
                onClick={() => navigate("/hospital/pharmacy/returns")}
                sx={{ fontSize: '1.1rem' }}
              >
                Back to Returns
              </Button>
              <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                Create New Return
              </Typography>
            </Box>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '1.1rem', fontWeight: 'bold' } }}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          {/* Step Content */}
          {activeStep === 0 && showInvoiceSearch && (
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                  Select Sale Invoice
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by invoice number, patient name, or phone..."
                        value={searchInvoice}
                        onChange={(e) => handleSearchInvoice(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ fontSize: '1.3rem' }} />
                            </InputAdornment>
                          ),
                          style: { fontSize: '1.1rem' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        align="center"
                        sx={{ fontSize: '1.2rem' }}
                      >
                        Total: {pagination.total} invoices
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {invoices.length > 0 && (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Invoice #</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Patient</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Amount</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Paid</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Due</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoices.map((invoice) => (
                            <TableRow key={invoice._id} hover>
                              <TableCell>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                  {invoice.invoiceNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {invoice.patient && (
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                    {invoice.patient.firstName}{" "}
                                    {invoice.patient.lastName}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                  {new Date(invoice.saleDate).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                                  ₹{invoice.grandTotal}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`₹${invoice.paidAmount}`}
                                  color={
                                    invoice.paidAmount === invoice.grandTotal
                                      ? "success"
                                      : "warning"
                                  }
                                  size="medium"
                                  sx={{ 
                                    fontSize: '1rem',
                                    '& .MuiChip-label': { fontSize: '1rem' }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`₹${invoice.dueAmount}`}
                                  color={
                                    invoice.dueAmount > 0 ? "error" : "default"
                                  }
                                  size="medium"
                                  sx={{ 
                                    fontSize: '1rem',
                                    '& .MuiChip-label': { fontSize: '1rem' }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="large"
                                  onClick={() => handleInvoiceSelect(invoice)}
                                  sx={{ fontSize: '1.1rem' }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination for invoices */}
                    {pagination.pages > 1 && (
                      <Box
                        sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                      >
                        <Pagination
                          count={pagination.pages}
                          page={pagination.page}
                          onChange={handlePageChange}
                          color="primary"
                          showFirstButton
                          showLastButton
                          size="large"
                        />
                      </Box>
                    )}
                  </>
                )}

                {invoices.length === 0 && searchInvoice && (
                  <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
                    No invoices found matching your search criteria.
                  </Alert>
                )}

                {!searchInvoice && (
                  <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
                    Enter invoice number, patient name, or phone number to search
                    for invoices.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 2: Select Items */}
          {activeStep === 1 && eligibilityData && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      Select Items to Return
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2, fontSize: '1.1rem' }}>
                      Select the items and quantities you want to return. Provide a
                      reason for each return.
                    </Alert>

                    {/* General Return Reason */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel sx={{ fontSize: '1.1rem' }}>General Return Reason</InputLabel>
                      <Select
                        value={returnReason}
                        label="General Return Reason"
                        onChange={(e) => handleGeneralReasonChange(e.target.value)}
                        sx={{ fontSize: '1.1rem' }}
                      >
                        <MenuItem value="expired" sx={{ fontSize: '1.1rem' }}>Expired</MenuItem>
                        <MenuItem value="damaged" sx={{ fontSize: '1.1rem' }}>Damaged</MenuItem>
                        <MenuItem value="wrong_medicine" sx={{ fontSize: '1.1rem' }}>Wrong Medicine</MenuItem>
                        <MenuItem value="patient_refused" sx={{ fontSize: '1.1rem' }}>Patient Refused</MenuItem>
                        <MenuItem value="side_effects" sx={{ fontSize: '1.1rem' }}>Side Effects</MenuItem>
                        <MenuItem value="other" sx={{ fontSize: '1.1rem' }}>Other</MenuItem>
                      </Select>
                    </FormControl>

                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Medicine</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Sold</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Available</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Return Qty</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Return Reason</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Damaged</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Refund/Unit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {returnItems.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                  {item.medicine.name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {item.medicine.genericName} •{" "}
                                  {item.batchStock?.batchNumber}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                                  ₹{item.sellingPrice} per unit
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                  {item.soldQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body1"
                                  color={
                                    item.remainingQuantity > 0
                                      ? "success.main"
                                      : "error.main"
                                  }
                                  sx={{ fontSize: '1.1rem' }}
                                >
                                  {item.remainingQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  <IconButton
                                    size="large"
                                    onClick={() =>
                                      handleReturnQuantityChange(
                                        index,
                                        item.returnQuantity - 1
                                      )
                                    }
                                    disabled={item.returnQuantity <= 0}
                                  >
                                    <Remove sx={{ fontSize: '1.3rem' }} />
                                  </IconButton>
                                  <Typography
                                    variant="body1"
                                    sx={{ minWidth: 30, textAlign: "center", fontSize: '1.2rem', fontWeight: 'bold' }}
                                  >
                                    {item.returnQuantity}
                                  </Typography>
                                  <IconButton
                                    size="large"
                                    onClick={() =>
                                      handleReturnQuantityChange(
                                        index,
                                        item.returnQuantity + 1
                                      )
                                    }
                                    disabled={
                                      item.returnQuantity >= item.remainingQuantity
                                    }
                                  >
                                    <Add sx={{ fontSize: '1.3rem' }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={item.returnReason}
                                  onChange={(e) =>
                                    handleReasonChange(index, e.target.value)
                                  }
                                  placeholder="Reason for return..."
                                  disabled={item.returnQuantity === 0}
                                  InputProps={{ style: { fontSize: '1.1rem' } }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant={
                                    item.isDamaged ? "contained" : "outlined"
                                  }
                                  color={item.isDamaged ? "error" : "primary"}
                                  size="large"
                                  onClick={() =>
                                    handleDamageChange(index, !item.isDamaged)
                                  }
                                  disabled={item.returnQuantity === 0}
                                  sx={{ fontSize: '1rem' }}
                                >
                                  {item.isDamaged ? "Yes" : "No"}
                                </Button>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                                  ₹{item.maxRefundPerUnit.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ position: "sticky", top: 100 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      Return Summary
                    </Typography>

                    {eligibilityData.saleInvoice.patient && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                          Patient
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{ fontSize: '1.1rem' }}>
                          {eligibilityData.saleInvoice.patient.firstName}{" "}
                          {eligibilityData.saleInvoice.patient.lastName}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                          {eligibilityData.saleInvoice.patient.phone}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                        Invoice Number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" sx={{ fontSize: '1.1rem' }}>
                        {eligibilityData.saleInvoice.invoiceNumber}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                        Payment Status
                      </Typography>
                      <Chip
                        label={eligibilityData.saleInvoice.paymentStatus.replace(
                          "_",
                          " "
                        )}
                        color={
                          eligibilityData.saleInvoice.paymentStatus === "paid"
                            ? "success"
                            : eligibilityData.saleInvoice.paymentStatus ===
                              "partially_paid"
                              ? "warning"
                              : "error"
                        }
                        size="medium"
                        sx={{ 
                          fontSize: '1rem',
                          '& .MuiChip-label': { fontSize: '1rem', fontWeight: 'bold' }
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                        Current Payment
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Paid:{" "}
                        <strong>
                          ₹{eligibilityData.saleInvoice.paidAmount.toFixed(2)}
                        </strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        Due:{" "}
                        <strong>
                          ₹{eligibilityData.saleInvoice.dueAmount.toFixed(2)}
                        </strong>
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Items to Return: <strong>{itemCount}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Return Value:{" "}
                        <strong>₹{totalReturnValue.toFixed(2)}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Actual Refund:{" "}
                        <strong>₹{actualRefundAmount.toFixed(2)}</strong>
                      </Typography>
                      <Divider sx={{ my: 2 }} />

                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                        Payment Impact:
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Paid:{" "}
                        <strong>₹{paymentUpdate.currentPaid.toFixed(2)}</strong> →{" "}
                        <strong>₹{paymentUpdate.newPaid.toFixed(2)}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        Due: <strong>₹{paymentUpdate.currentDue.toFixed(2)}</strong>{" "}
                        → <strong>₹{paymentUpdate.newDue.toFixed(2)}</strong>
                      </Typography>
                    </Box>

                    <Alert
                      severity={actualRefundAmount > 0 ? "success" : "info"}
                      sx={{ mb: 3, fontSize: '1.1rem' }}
                    >
                      {paymentUpdate.paymentStatus === "paid"
                        ? "Full refund will be issued"
                        : paymentUpdate.paymentStatus === "partially_paid"
                          ? actualRefundAmount > 0
                            ? "Due amount settled first, remaining will be refunded"
                            : "Return value will be adjusted against due amount only"
                          : "Return value will be adjusted against due amount"}
                    </Alert>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        onClick={handleBack}
                        sx={{ fontSize: '1.1rem', py: 1.5 }}
                      >
                        Back
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleNext}
                        disabled={itemCount === 0 || !returnReason}
                        sx={{ fontSize: '1.1rem', py: 1.5 }}
                      >
                        Configure Refund
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Step 3: Configure Refund */}
          {activeStep === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      Configure Refund Settings
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={applyOriginalDiscount}
                          onChange={(e) =>
                            setApplyOriginalDiscount(e.target.checked)
                          }
                          color="primary"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.1rem' }}>
                          Apply original discount rates to returned items
                        </Typography>
                      }
                      sx={{ mb: 3 }}
                    />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel sx={{ fontSize: '1.1rem' }}>Refund Method</InputLabel>
                      <Select
                        value={refundMethod}
                        label="Refund Method"
                        onChange={(e) => setRefundMethod(e.target.value)}
                        sx={{ fontSize: '1.1rem' }}
                      >
                        <MenuItem value="cash" sx={{ fontSize: '1.1rem' }}>Cash</MenuItem>
                        <MenuItem value="bank_transfer" sx={{ fontSize: '1.1rem' }}>Bank Transfer</MenuItem>
                        <MenuItem value="credit_note" sx={{ fontSize: '1.1rem' }}>Credit Note</MenuItem>
                        <MenuItem value="wallet" sx={{ fontSize: '1.1rem' }}>Wallet Credit</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Custom Refund Amount (Optional)"
                      type="number"
                      value={customRefundAmount}
                      onChange={(e) => setCustomRefundAmount(e.target.value)}
                      helperText={`Maximum allowed refund: ₹${actualRefundAmount.toFixed(
                        2
                      )}`}
                      sx={{ mb: 2 }}
                      error={
                        customRefundAmount &&
                        parseFloat(customRefundAmount) > actualRefundAmount
                      }
                      InputProps={{ style: { fontSize: '1.1rem' } }}
                      InputLabelProps={{ style: { fontSize: '1.1rem' } }}
                      FormHelperTextProps={{ style: { fontSize: '1rem' } }}
                    />

                    <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
                      {customRefundAmount
                        ? `Custom refund amount set to ₹${parseFloat(
                          customRefundAmount
                        ).toFixed(2)}`
                        : `Using calculated refund amount: ₹${actualRefundAmount.toFixed(
                          2
                        )}`}
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ position: "sticky", top: 100 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      Refund Summary
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Return Value:{" "}
                        <strong>₹{totalReturnValue.toFixed(2)}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Calculated Refund:{" "}
                        <strong>₹{actualRefundAmount.toFixed(2)}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Final Refund:{" "}
                        <strong>₹{finalRefundAmount.toFixed(2)}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        Cost Recovery: <strong>₹{totalCost.toFixed(2)}</strong>
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body1"
                        color={netGainLoss < 0 ? "error.main" : "success.main"}
                        sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}
                      >
                        Net Impact: <strong>₹{netGainLoss.toFixed(2)}</strong>
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        onClick={handleBack}
                        sx={{ fontSize: '1.1rem', py: 1.5 }}
                      >
                        Back
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleNext}
                        disabled={finalRefundAmount > actualRefundAmount}
                        sx={{ fontSize: '1.1rem', py: 1.5 }}
                      >
                        Review Return
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Step 4: Review & Submit */}
          {activeStep === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      Review Return
                    </Typography>

                    <Alert severity="success" sx={{ mb: 3, fontSize: '1.1rem' }} icon={<CheckCircle sx={{ fontSize: '1.5rem' }} />}>
                      Please review the return details before submitting for
                      approval.
                    </Alert>

                    {/* Return Items Summary */}
                    <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: '1.3rem', mb: 2 }}>
                      Items to Return ({itemCount})
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Medicine</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Quantity</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Reason</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Damaged</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Refund Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itemsToReturn.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                  {item.medicine.name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {item.medicine.genericName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                  {item.returnQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                  {item.returnReason}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={item.isDamaged ? "Yes" : "No"}
                                  color={item.isDamaged ? "error" : "default"}
                                  size="medium"
                                  sx={{ 
                                    fontSize: '1rem',
                                    '& .MuiChip-label': { fontSize: '1rem' }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                                  ₹{item.itemTotalValue.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Refund Summary */}
                    <Card variant="outlined" sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem' }}>
                          Refund Summary
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: '1.1rem' }}>Total Return Value:</Typography>
                          <Typography sx={{ fontSize: '1.1rem' }}>₹{totalReturnValue.toFixed(2)}</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: '1.1rem' }}>Final Refund Amount:</Typography>
                          <Typography fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                            ₹{finalRefundAmount.toFixed(2)}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                          sx={{ fontSize: '1.2rem' }}
                        >
                          Payment Impact:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>Paid Amount:</Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                            ₹{paymentUpdate.currentPaid.toFixed(2)} → ₹
                            {paymentUpdate.newPaid.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 3,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>Due Amount:</Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                            ₹{paymentUpdate.currentDue.toFixed(2)} → ₹
                            {paymentUpdate.newDue.toFixed(2)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
                        >
                          <Typography sx={{ fontSize: '1.1rem' }}>Refund Method:</Typography>
                          <Typography fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            {refundMethod.replace("_", " ")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography sx={{ fontSize: '1.1rem' }}>Return Reason:</Typography>
                          <Typography fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            {returnReason.replace("_", " ")}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Button 
                        onClick={handleBack} 
                        variant="outlined"
                        sx={{ fontSize: '1.1rem', py: 1.5, px: 3 }}
                      >
                        Back to Refund
                      </Button>
                      <Button
                        onClick={() => setConfirmationOpen(true)}
                        variant="contained"
                        size="large"
                        startIcon={<Save sx={{ fontSize: '1.3rem' }} />}
                        sx={{ fontSize: '1.1rem', py: 1.5, px: 3 }}
                      >
                        Submit Return Request
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmationOpen}
            onClose={() => setConfirmationOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: '1.5rem' }}>Confirm Return Request</DialogTitle>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 2, fontSize: '1.1rem' }}>
                This return request will require manager approval before processing.
              </Alert>
              <Typography variant="body1" gutterBottom sx={{ fontSize: '1.1rem' }}>
                Are you sure you want to submit this return request?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                Total Return Value: <strong>₹{totalReturnValue.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                Final Refund Amount:{" "}
                <strong>₹{finalRefundAmount.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                Items: <strong>{itemCount}</strong>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                Return Reason: <strong>{returnReason.replace("_", " ")}</strong>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                Payment Impact: Paid ₹{paymentUpdate.currentPaid.toFixed(2)} → ₹
                {paymentUpdate.newPaid.toFixed(2)}, Due ₹
                {paymentUpdate.currentDue.toFixed(2)} → ₹
                {paymentUpdate.newDue.toFixed(2)}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setConfirmationOpen(false)}
                sx={{ fontSize: '1.1rem' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitReturn}
                variant="contained"
                disabled={saving}
                startIcon={saving ? null : <CheckCircle sx={{ fontSize: '1.3rem' }} />}
                sx={{ fontSize: '1.1rem', py: 1 }}
              >
                {saving ? "Submitting..." : "Submit Return Request"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Box>
  );
};